import type { ReactNode } from "react";
import { CheckboxFace } from "./Checkbox";

// CheckableCard — a full-width bordered card row wrapping a `Checkbox`. The
// card-chrome counterpart to the inline `Checkbox`: --interactive-soft tint and a
// 1px --interactive border when checked, neutral when disabled
// — see the checked branch below for the contrast numbers. Use for
// multi-select lists and consent rows where each option wants its own
// enclosure.
//
// THE CARD IS THE BUTTON. It used to be a styling <div> holding the `Checkbox`
// button, stretched with `[&>button]:w-full` and commented as making "the whole
// padded row a click target". It did not: `w-full` fills the CONTENT box, and the
// card's own p-4 ring sits outside it — so the 16px band around the row looked
// clickable and was dead. Hosting `CheckboxFace` inside one button here puts the
// padding inside the target, which is what the old comment claimed.
//
// Not a <label> (the pattern RadioCard uses) because there is no input to
// associate: this control is a button with role="checkbox", and a label wrapping
// no input labels nothing. `multiline` switches the row from center-aligned to
// top-aligned for tall (wrapping) labels.
//
// Colors use the dova-ds unlayered utility classes so the `.dova-*` cascade
// does not fight a competing arbitrary-value class. Presentation-only.

export interface CheckableCardProps {
    label: ReactNode;
    checked: boolean;
    onChange: (checked: boolean) => void;
    disabled?: boolean;
    multiline?: boolean;
    /** Forwarded to the inner face. `lg` (24px box, 16px gap) is for a card
     *  whose label runs to a title plus a description. */
    size?: "md" | "lg";
}

export function CheckableCard({ label, checked, onChange, disabled, multiline, size }: CheckableCardProps) {
    return (
        <button
            type="button"
            role="checkbox"
            aria-checked={checked}
            disabled={disabled}
            onClick={() => !disabled && onChange(!checked)}
            className={[
                "flex w-full gap-4 rounded-[var(--radius-md)] text-left select-none",
                "transition-[border-color,background-color,padding] duration-[var(--dur-base)] ease-[var(--ease-out)]",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-interactive focus-visible:ring-offset-2",
                disabled ? "text-tertiary" : "cursor-pointer text-primary",
                multiline ? "items-start" : "items-center",
                // --canvas, not --muted. --muted is what the checkbox INSIDE this row
                // used to fill with too, so a disabled row and its own control were the
                // same colour and the box disappeared — the one thing the row is about
                // stopped being visible at the moment it most needed explaining. The
                // box is --surface now and the row is --canvas, so the two separate.
                disabled
                    ? "cursor-not-allowed border border-border bg-canvas p-4 opacity-60"
                    // Checked = 1px --interactive border over the --interactive-soft
                    // tint — the one selected style every DS selection control shares
                    // (operator, 2026-09-15). That restores the tint a 2026-09 QA pass
                    // had removed here as "the same signal twice": consistency across
                    // controls won over the redundancy argument.
                    //
                    // ⚠ KNOWN WCAG 1.4.11 GAP, ACCEPTED: --interactive measures ~2.5:1
                    // against the tint and canvas, under 3:1. A 2px --success border was
                    // reverted on review as too dark — see RadioCard for the full note.
                    : checked
                      ? "border border-interactive bg-interactive-soft p-4"
                      : "border border-border bg-surface p-4 hover:border-interactive",
            ].join(" ")}
        >
            <CheckboxFace
                checked={checked}
                disabled={disabled}
                label={label}
                multiline={multiline}
                size={size}
            />
        </button>
    );
}
